import { useEffect } from "react";

/**
 * The custom property the app shell sets to the height of the banners it draws above the app bar: the
 * system notices and the cookie consent banner (#5168), or `0px` when none is showing.
 *
 * A drawer's paper is fixed to the window, so it finds the bottom of the app bar by the app bar's
 * height alone. That holds only while the app bar is at the top of the window; a banner pushes it
 * down, and the paper then starts behind the app bar with its title and first entries hidden. Adding
 * this height puts the paper back below it.
 *
 * Set on the document's root element rather than on the shell's: a temporary drawer is portaled to the
 * body, outside the shell, and would not inherit it from there.
 */
export const SHELL_TOP_BANNERS_HEIGHT_PROPERTY = "--shell-top-banners-height";

/** The banners' height where a style needs it, and nothing where the shell sets none. */
export function shellTopBannersHeight(): string {
  return `var(${SHELL_TOP_BANNERS_HEIGHT_PROPERTY}, 0px)`;
}

/**
 * Keeps the property at the height of `banners`, the element that holds them, for as long as it is
 * mounted. A banner is dismissed, and its text wraps differently as the window narrows, so the height
 * is observed rather than read once.
 */
export function useShellTopBannersHeight(banners: HTMLElement | null) {
  useEffect(() => {
    if (!banners || typeof ResizeObserver === "undefined") return undefined;

    const rootStyle = document.documentElement.style;
    const observer = new ResizeObserver(() => {
      rootStyle.setProperty(SHELL_TOP_BANNERS_HEIGHT_PROPERTY, `${banners.getBoundingClientRect().height}px`);
    });
    observer.observe(banners);
    return () => {
      observer.disconnect();
      rootStyle.removeProperty(SHELL_TOP_BANNERS_HEIGHT_PROPERTY);
    };
  }, [banners]);
}
